import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Checkbox, TextField, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const SortableTask = ({ 
  task, 
  index, 
  onTaskChange,
  onTaskToggle,
  onAddTask,
  onSplitTask,
  onJoinTask,
  onDeleteTask,
  onNavigateTask,
  editMode,
  isDragDisabled = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: task.id || `temp-${index}`, 
    disabled: isDragDisabled // Allow drag for tasks with temp IDs
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'transform 0.2s ease-in-out',
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 999 : 'auto',
  };

  const handleKeyDown = (e) => {
    const { key, target, shiftKey, ctrlKey, metaKey } = e;
    const { selectionStart, selectionEnd, value } = target;
    
    // Handle different key combinations
    switch (key) {
      case 'Enter':
        if (shiftKey) {
          // Shift+Enter: Allow default behavior (new line)
          return;
        } else {
          // Enter: Split task or create new task
          e.preventDefault();
          handleEnterKey(selectionStart, value);
        }
        break;
        
      case 'Backspace':
        if (selectionStart === 0 && selectionEnd === 0) {
          // Backspace at beginning: Join with previous task
          e.preventDefault();
          handleBackspaceKey();
        }
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        handleArrowUp(selectionStart, value);
        break;
        
      case 'ArrowDown':
        e.preventDefault();
        handleArrowDown(selectionEnd, value);
        break;
        
      case 'z':
        if ((ctrlKey || metaKey) && !shiftKey) {
          // Ctrl/Cmd+Z: Undo
          e.preventDefault();
          onNavigateTask?.('undo');
        }
        break;
        
      default:
        break;
    }
  };

  const handleEnterKey = (cursorPosition, text) => {
    if (cursorPosition === text.length) {
      // At end of text: create new empty task
      onAddTask?.();
    } else {
      // In middle of text: split task
      onSplitTask?.(index, cursorPosition, text);
    }
  };

  const handleBackspaceKey = () => {
    if (index > 0) {
      onJoinTask?.(index);
    }
  };

  const handleArrowUp = (cursorPosition, text) => {
    if (cursorPosition === 0) {
      // At beginning: move to end of previous task
      onNavigateTask?.('up', index, 'end');
    } else {
      // Not at beginning: move to beginning of current task
      onNavigateTask?.('up', index, 'beginning');
    }
  };

  const handleArrowDown = (cursorPosition, text) => {
    if (cursorPosition === text.length) {
      // At end: move to beginning of next task
      onNavigateTask?.('down', index, 'beginning');
    } else {
      // Not at end: move to end of current task
      onNavigateTask?.('down', index, 'end');
    }
  };

  const handleDelete = () => {
    onDeleteTask?.(index);
  };

  return (
    <Box 
      ref={setNodeRef} 
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 0.5, // Reduced spacing for compact view
        minHeight: '24px', // Slightly smaller
        '&:hover .drag-handle': {
          opacity: !isDragDisabled ? 1 : 0
        }
      }}
    >
      {/* Drag Handle */}
      <Box 
        className="drag-handle"
        {...attributes}
        {...listeners}
        sx={{
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out',
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '16px',
          height: '20px',
          mr: 0.5,
          color: 'text.secondary',
          fontSize: '14px',
          lineHeight: 1,
          userSelect: 'none',
          touchAction: 'none',
          '&:hover': {
            color: 'text.primary'
          }
        }}
      >
        ⋮⋮
      </Box>

      {/* Checkbox */}
      <Checkbox 
        checked={task.completed}
        onChange={() => onTaskToggle ? onTaskToggle(index, task.id, task.completed) : null}
        sx={{ 
          mr: 1,
          transform: 'scale(0.85)', // Slightly smaller to match compact design
        }}
        disabled={!onTaskToggle}
        size="small"
      />

      {/* Task Text Field */}
      <TextField
        id={`task-${index}`}
        fullWidth
        variant="standard"
        size="small"
        multiline
        value={task.text}
        onChange={(e) => onTaskChange(index, e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Enter task description"
        InputProps={{
          disableUnderline: true
        }}
        sx={{
          '& textarea, & input': {
            textDecoration: task.completed ? 'line-through' : 'none',
            color: task.completed ? 'text.secondary' : 'text.primary',
            fontSize: '0.875rem', // Slightly smaller font for compact view
            lineHeight: 1.3,
            py: 0.25, // Reduced padding
          }
        }}
      />

      {/* Delete Icon */}
      {isHovered && (
        <IconButton
          size="small"
          onClick={handleDelete}
          sx={{
            ml: 0.5,
            opacity: 0.7,
            transition: 'opacity 0.2s ease-in-out',
            '&:hover': {
              opacity: 1,
              color: 'error.main'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default SortableTask;